import { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { usePopupStore } from "../..";
import useNavigateToLeaseComp from "../../hooks/useNavigateToLeaseComp";
import useNavigateToLocation from "../../hooks/useNavigateToLocation";
import useNavigateToSaleComp from "../../hooks/useNavigateToSaleComp";
import INearestLocation, { NearestLocationType } from "../../interfaces/INearestLocation";
import PopupType from "../../interfaces/PopupType";
import { distinctBy, formatNumber } from "../../utils/utils";
import commonStyles from "../Common.module.css";
import Grid from "../Grid";

function NearestLocationsGrid({ locations, radiusDropdown }: INearestLocationsGridProps) {
    const popupStore = usePopupStore();
    const navigateToLocation = useNavigateToLocation();
    const navigateToLeaseComp = useNavigateToLeaseComp();
    const navigateToSaleComp = useNavigateToSaleComp();

    const companyCount = useMemo(() => distinctBy(locations, l => l.pipedriveCompanyId).length, [locations]);

    return (
        <Grid
            data={locations}
            headers={[
                {
                    align: "left",
                    titleStrings: ["COMPANY NAME"],
                    getValue: item => item.companyName,
                    getLabel: item => item.companyName,
                },
                {
                    titleStrings: ["# LOCATIONS"],
                    getValue: item => item.companyLocationCount,
                    getLabel: item => String(item.companyLocationCount),
                },
                {
                    titleStrings: ["TYPE"],
                    getValue: item => item.type,
                    getLabel: item => item.type,
                },
                {
                    align: "left",
                    titleStrings: ["SITE NAME"],
                    getValue: item => item.siteName ?? "",
                    getLabel: item => item.siteName ?? "",
                },
                {
                    align: "left",
                    isFullWidth: true,
                    titleStrings: ["ADDRESS"],
                    getValue: item => item.address,
                    getLabel: item => item.address,
                },
                {
                    titleStrings: ["DISTANCE"],
                    getValue: item => item.distance,
                    getLabel: item => formatNumber(item.distance),
                },
            ]}
            getId={item => item.rowId}
            downloadFileName={"Companies"}
            onRowClick={item => {
                if (item.type === NearestLocationType.Location)
                    navigateToLocation(item.entityId);
                else if (item.type === NearestLocationType.LeaseComp)
                    navigateToLeaseComp(item.entityId);
                else
                    navigateToSaleComp(item.entityId);
            }}
            isRowActive={item => {
                if (item.type === NearestLocationType.Location)
                    return popupStore.type === PopupType.Location && popupStore.id === item.entityId;
                if (item.type === NearestLocationType.LeaseComp)
                    return popupStore.type === PopupType.LeaseComp && popupStore.id === item.entityId;
                return popupStore.type === PopupType.SaleComp && popupStore.id === item.entityId;
            }}
            header={<div className={[commonStyles.gridTitle, commonStyles.header].join(" ")}>
                {companyCount} Companies
                {radiusDropdown}
            </div>}
        />
    );
}

export default observer(NearestLocationsGrid);

interface INearestLocationsGridProps {
    locations: INearestLocation[];
    radiusDropdown?: React.ReactNode;
}
